<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'next/button/Button.vue';
import TabBar from 'next/tabbar/TabBar.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { accountScopedRoute } = useAccount();

const currentView = ref(null);

const tabs = computed(() => [
  {
    key: 'reports',
    label: t('WGPT_CUSTOM_REPORTS.SETTINGS_TABS.REPORTS'),
    routeName: 'wgpt_reports_settings_list',
    active: [
      'wgpt_reports_settings_list',
      'wgpt_reports_settings_create',
      'wgpt_reports_settings_edit',
    ],
  },
  {
    key: 'dashboards',
    label: t('WGPT_CUSTOM_REPORTS.SETTINGS_TABS.DASHBOARDS'),
    routeName: 'wgpt_settings_custom_dashboards_list',
    active: [
      'wgpt_settings_custom_dashboards_list',
      'wgpt_settings_custom_dashboards_create',
      'wgpt_settings_custom_dashboards_edit',
    ],
  },
  {
    key: 'definitions',
    label: t('WGPT_CUSTOM_REPORTS.SETTINGS_TABS.EVENT_DEFINITIONS'),
    routeName: 'wgpt_reports_settings_event_definitions_list',
    active: ['wgpt_reports_settings_event_definitions_list'],
  },
  {
    key: 'enrichments',
    label: t('WGPT_CUSTOM_REPORTS.SETTINGS_TABS.ENRICHMENTS'),
    routeName: 'wgpt_reports_settings_enrichments_list',
    active: ['wgpt_reports_settings_enrichments_list'],
  },
]);

const tabBarTabs = computed(() =>
  tabs.value.map(tab => ({ label: tab.label, key: tab.key }))
);

const activeTabIndex = computed(() => {
  const idx = tabs.value.findIndex(tab => tab.active.includes(route.name));
  return idx === -1 ? 0 : idx;
});

const activeTabKey = computed(() => tabs.value[activeTabIndex.value]?.key);

const onTabChange = tab => {
  const found = tabs.value.find(({ key }) => key === tab.key);
  if (found) router.push(accountScopedRoute(found.routeName));
};

const onCreate = () => {
  if (activeTabKey.value === 'reports') {
    router.push(accountScopedRoute('wgpt_reports_settings_create'));
  } else if (activeTabKey.value === 'dashboards') {
    router.push(accountScopedRoute('wgpt_settings_custom_dashboards_create'));
  } else if (activeTabKey.value === 'definitions') {
    currentView.value?.startCreate?.();
  } else if (activeTabKey.value === 'enrichments') {
    currentView.value?.openCreateDialog?.();
  }
};
</script>

<template>
  <div
    class="flex flex-col w-full h-full m-0 pb-8 pt-4 px-6 overflow-auto bg-n-surface-1"
  >
    <div class="flex items-start w-full max-w-5xl mx-auto">
      <SettingsLayout>
        <template #header>
          <BaseSettingsHeader
            :title="t('WGPT_CUSTOM_REPORTS.SETTINGS_HEADER')"
            :description="t('WGPT_CUSTOM_REPORTS.DESCRIPTION')"
            feature-name="custom_reports"
          >
            <template #tabs>
              <TabBar
                :tabs="tabBarTabs"
                :initial-active-tab="activeTabIndex"
                @tab-changed="onTabChange"
              />
            </template>
            <template #actions>
              <Button
                v-if="activeTabKey === 'reports'"
                icon="i-lucide-plus"
                :label="t('WGPT_CUSTOM_REPORTS.CREATE')"
                size="sm"
                @click="onCreate"
              />
              <Button
                v-else-if="activeTabKey === 'dashboards'"
                icon="i-lucide-plus"
                :label="t('WGPT_CUSTOM_DASHBOARDS.CREATE')"
                size="sm"
                @click="onCreate"
              />
              <Button
                v-else-if="activeTabKey === 'definitions'"
                icon="i-lucide-plus"
                :label="t('WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.CREATE')"
                size="sm"
                @click="onCreate"
              />
              <Button
                v-else-if="activeTabKey === 'enrichments'"
                icon="i-lucide-plus"
                :label="t('WGPT_CUSTOM_REPORTS.ENRICHMENTS.CREATE')"
                size="sm"
                @click="onCreate"
              />
            </template>
          </BaseSettingsHeader>
        </template>

        <template #body>
          <section class="flex-1 overflow-auto mt-4">
            <router-view v-slot="{ Component }">
              <component :is="Component" ref="currentView" />
            </router-view>
          </section>
        </template>
      </SettingsLayout>
    </div>
  </div>
</template>
